// ---------------------------------------------------------------------------
// The pages outside the console, on the design system's AuthLayout (R13)
//
// Sign-in, the admin sign-in (German), the opt-out page reached from every
// email and the page a provider sends the person back to after connecting
// an account: each is one AuthLayout (design/auth-layout.ts). This module
// gives them what they share:
//   - NDI's full logo, as NDI supplied it: the file with the black line for
//     light grounds and the one with the white line for dark grounds (what
//     ndiLogo() showed before, ndi-brand.ts), at the 120px the logo needs;
//   - under the card, the legal line and the two German legal texts, which
//     open the same dialog as the console's footer (legal-dialog.ts). They
//     were missing from these pages; German law wants the imprint one click
//     away from every page. Their labels carry lang="de" on an English page.
//
// These pages sit on the system's own 14/22 base: none of them is wrapped in
// the legacy island (.ndi-legacy, R12b2; shell/outlet.ts leaves them out).
//
// Each one scrolls itself. initApp fixes #root to the window's height and sets
// <html> to overflow hidden (functional-lit init-app.js); the console scrolls
// inside its main, and these pages had nothing that scrolls: on a phone held
// sideways the legal texts, and with less height the sign-in's button, were
// out of reach (measured in Chrome at 844×390). So the AuthLayout sits in a box
// that fills the root and scrolls: layout, in Tailwind (7.2), around the port
// and not through its class (components.css is unlayered and would win).
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { type AuthLayoutProps, authLayout, type LogoSource } from '../design/auth-layout.js';
import { showImprint, showPrivacyPolicy } from '../legal-dialog.js';

/** NDI's full logo: the light-ground file, and the dark-ground file for Dark. */
export const NDI_LOGO: LogoSource = { src: '/ndi-logo-light.png', darkSrc: '/ndi-logo.png' };

/** The legal line under the card. */
export const legalLine = () => `© ${new Date().getFullYear()} New Digital Intelligence GmbH`;

export type StandalonePageProps = Pick<AuthLayoutProps, 'title' | 'description' | 'lang' | 'size' | 'className'>;

/** A page outside the console: AuthLayout with NDI's logo and the legal texts. */
export function standalonePage(p: StandalonePageProps, content?: unknown): TemplateResult {
  return html`<div class="h-full overflow-y-auto">
    ${authLayout(
      {
        ...p,
        logo: NDI_LOGO,
        legal: legalLine(),
        links: [
          { label: html`<span lang="de">Datenschutzerklärung</span>`, onClick: () => void showPrivacyPolicy() },
          { label: html`<span lang="de">Impressum</span>`, onClick: () => void showImprint() },
        ],
      },
      content,
    )}
  </div>`;
}
